import React, { createContext, useContext, useEffect, useState } from "react";
import { getReportConfig } from "@/services/api/reports";
import type { ReportConfig } from "@/services/api/reports/types";

interface PDFConfigContextType {
  config: ReportConfig | null;
  isLoading: boolean;
  error: Error | null;
}

const PDFConfigContext = createContext<PDFConfigContextType | undefined>(
  undefined
);

interface PDFConfigProviderProps {
  configId: string;
  children: React.ReactNode;
}

export function PDFConfigProvider({
  configId,
  children,
}: PDFConfigProviderProps) {
  const [config, setConfig] = useState<ReportConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function loadConfig() {
      try {
        setIsLoading(true);
        setError(null);
        const reportConfig = await getReportConfig(configId);
        setConfig(reportConfig);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to load report configuration")
        );
      } finally {
        setIsLoading(false);
      }
    }

    if (configId) {
      loadConfig();
    }
  }, [configId]);

  return (
    <PDFConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </PDFConfigContext.Provider>
  );
}

export function usePDFConfig() {
  const context = useContext(PDFConfigContext);
  if (context === undefined) {
    throw new Error("usePDFConfig must be used within a PDFConfigProvider");
  }
  return context;
}
